import React, { useState, useEffect, useCallback, useMemo } from 'react';
import AddElementLayout from "../layouts/AddElementLayout";
import * as Yup from 'yup';
import TextInput from "../inputs/TextInput";
import SelectInput from "../inputs/SelectInput";
import FieldArrayInput from "../inputs/FieldArray";
import { Button, Col, Row, Card, Divider } from "antd";
import { PhoneOutlined, WhatsAppOutlined } from '@ant-design/icons';
import { openNotification } from "../../helpers/alerts";
import { updateData, getData } from "../../helpers/api";
import { phoneRegExp, confirmationStatus, DeliveryCompaniesConst, confirmationRoles, rolesConst } from "../../constants/others";
import { getLocalStorage } from "../../helpers/localstorage";

const ConfirmOrderForm = ({
    order,
    isModalOpen,
    handleCancel,
    onOrderConfirmed
}) => {
    const [loading, setLoading] = useState(false);
    const [products, setProducts] = useState([]);
    const [deliveryCompanies, setDeliveryCompanies] = useState([]);
    const [deliveryCompaniesAccounts, setDeliveryCompaniesAccounts] = useState([]);
    const [selectedDeliveryCompaniesAccounts, setSelectedDeliveryCompaniesAccounts] = useState([]);
    const [deliveryMen, setDeliveryMen] = useState([]);
    const [selectedConfirmationStatus, setSelectedConfirmationStatus] = useState(null);

    // Get cities from localStorage
    const cities = useMemo(() => getLocalStorage("bmdCities") || [], []);

    // Fetch products data
    const getProducts = useCallback(async () => {
        try {
            const user = getLocalStorage("auth")?.user;
            if ([...confirmationRoles, rolesConst.Management].includes(user?.role)) {
                // For roles that need product filtering, get allowed products
                const res = await getData(`/users/${user.id}/allowed-products`);
                setProducts(res.data.map((product) => ({ value: product.id, label: product.name })));
            } else {
                // For other roles (like super admin), get all products
                const res = await getData("/products");
                setProducts(res.data.map((product) => ({ value: product.id, label: product.name })));
            }
        } catch (error) {
            console.log(error);
        }
    }, []);

    // Fetch delivery companies data
    const getDeliveryCompanies = useCallback(async () => {
        try {
            const { data } = await getData("/users/delivery/companies/accounts");
            setDeliveryCompanies(data.deliveryCompanies.map((el) => ({ label: el.name, value: el.id })));
            setDeliveryCompaniesAccounts(data.deliveryCompanyKeys.map((el) => ({
                label: el.name,
                value: el.id,
                deliveryCompanyId: el.deliveryCompany.id
            })));
        } catch (error) {
            console.log(error);
        }
    }, []);

    // Fetch delivery men
    const getDeliveryMen = useCallback(async () => {
        try {
            const res = await getData("/users/deliverymen");
            setDeliveryMen(res.data.map((deliveryMan) => ({ value: deliveryMan.id, label: deliveryMan.name })));
        } catch (error) {
            console.log("error fetching delivery men");
        }
    }, []);

    // Handle delivery company selection
    const handleDeliveryCompanies = useCallback((value) => {
        const selectedDeliveryCompany = deliveryCompanies.find((el) => el.value === value);
        if (selectedDeliveryCompany?.label === DeliveryCompaniesConst.DeliveryMen) {
            setSelectedDeliveryCompaniesAccounts(deliveryMen);
        } else {
            setSelectedDeliveryCompaniesAccounts(
                deliveryCompaniesAccounts.filter((el) => el.deliveryCompanyId === value)
            );
        }
    }, [deliveryCompanies, deliveryCompaniesAccounts, deliveryMen]);

    // Handle confirmation status change
    const handleConfirmationStatusChange = useCallback((value) => {
        setSelectedConfirmationStatus(value);
    }, []);

    useEffect(() => {
        if (isModalOpen) {
            getProducts();
            getDeliveryCompanies();
            getDeliveryMen();
        }
    }, [isModalOpen, getProducts, getDeliveryCompanies, getDeliveryMen]);

    const confirmOrder = async (values, submitProps) => {
        try {
            setLoading(true);

            const currentUser = getLocalStorage("auth")?.user;
            const currentDate = new Date().toISOString();

            // Prepare the data to send to the backend (sheet-orders update)
            const sheetOrderData = {
                name: values.name,
                phone: values.phone,
                address: values.address,
                city: values.city, // Use city as text for sheet orders
                price: parseFloat(values.price),
                orders: values.orders.map(order => ({
                    product: order.product,
                    unit: parseInt(order.unit)
                })),
                isTreated: true,
                confirmationEnd: currentDate, // Add confirmation end date when form is submitted
                // Include form values for order creation
                bmdCity: values.bmdCity,
                deliveryCompany: values.deliveryCompany,
                deliveryCompanyKey: values.deliveryCompanyKey,
                confimationStatus: values.confirmationStatus,
                user: currentUser?.id,
                note: values.note
            };

            await updateData(`/sheet-orders/${order.id}`, sheetOrderData);

            // Order creation is now handled in the backend sheet-orders service update method
            // The backend will create the order with all the data when confirmationEnd is set
            // No need to create order here anymore as it's done in the backend

            openNotification("Commande confirmée avec succès", "success");

            submitProps.resetForm();
            handleCancel();
            if (onOrderConfirmed) {
                onOrderConfirmed();
            }
        } catch (error) {
            console.error('Error confirming order:', error);
            openNotification("Erreur lors de la confirmation de la commande", "error");
        } finally {
            setLoading(false);
        }
    };

    // Prepare initial form values from the order
    const formInitialValues = {
        name: order?.name || '',
        phone: order?.phone || '',
        address: order?.address || '',
        city: order?.city || '', // Original city as text
        bmdCity: null, // No default value for city selection
        price: order?.price || 0,
        orders: [{
            product: order?.product?.id || undefined,
            unit: order?.unit || null
        }],
        confirmationStatus: null, // No default value for confirmation status
        deliveryCompany: null,
        deliveryCompanyKey: null,
        note:null
    };

    // Prepare confirmation status options
    const confirmationStatusOptions = confirmationStatus.map((status) => ({
        value: status,
        label: status
    }));

    // Dynamic validation schema based on confirmation status
    const getValidationSchema = () => {
        const baseSchema = {
            name: Yup.string().required('Le nom est requis'),
            phone: Yup.string()
                .required('Le téléphone est requis')
                .matches(phoneRegExp, 'Le numéro de téléphone doit commencer par 06 ou 07 et comporter 10 chiffres.'),
            address: Yup.string().required('L\'adresse est requise'),

            price: Yup.number()
                .required('Le prix est requis')
                .min(0, 'Le prix doit être positif'),
            orders: Yup.array().of(
                Yup.object().shape({
                    product: Yup.number().required('Le produit est requis'),
                    unit: Yup.number()
                        .required('L\'unité est requise')
                        .min(1, 'L\'unité doit être au moins 1'),
                })
            ).min(1, 'Au moins un produit est requis'),
            confirmationStatus: Yup.string().required('Le statut de confirmation est requis'),
        };

        // Add delivery validation if confirmationStatus is "confirmée"
        if (selectedConfirmationStatus === 'confirmée') {
            baseSchema.deliveryCompany = Yup.string().required('La société de livraison est requise');
            baseSchema.deliveryCompanyKey = Yup.string().required('Le compte de livraison est requis');
            baseSchema.bmdCity = Yup.string().required('La ville BMD est requise');
        }

        return Yup.object(baseSchema);
    };

    return (
        <AddElementLayout
            title="Confirmer la commande"
            isModalOpen={isModalOpen}
            handleCancel={handleCancel}
            initialValue={formInitialValues}
            width={600}
            validationSchema={getValidationSchema()}
            handleSubmitForm={(values, submitProps) => {
                confirmOrder(values, submitProps);
            }}
        >
            {/* Display Original Order Details */}
            <Card
                title="Détails de la commande originale"
                size="small"
                style={{ marginBottom: 20, backgroundColor: '#f9f9f9' }}
            >
                <Row gutter={[16, 8]}>
                    <Col span={12}><strong>Nom:</strong> {order?.name}</Col>
                    <Col span={12}>
                        <strong>Téléphone:</strong> {order?.phone}
                        <span style={{ marginLeft: '8px' }}>
                            <a target="_blank" href={`tel:${order?.phone}`} rel="noreferrer">
                                <PhoneOutlined style={{ fontSize: '16px', color: '#5546ff', marginRight: '4px' }} />
                            </a>
                            <a target="_blank" href={`https://wa.me/${order?.phone}`} rel="noreferrer">
                                <WhatsAppOutlined style={{ fontSize: '16px', color: '#0cc143' }} />
                            </a>
                        </span>
                    </Col>
                    <Col span={12}><strong>Ville:</strong> {order?.city}</Col>
                    <Col span={12}><strong>Prix:</strong> {order?.price} DH</Col>
                    <Col span={12}><strong>Unité:</strong> {order?.unit}</Col>
                    <Col span={12}><strong>Produit:</strong> {order?.productName}</Col>
                    <Col span={24}><strong>Adresse:</strong> {order?.address}</Col>
                    <Col span={12}><strong>Statut:</strong> {order?.isTreated ? "Traité" : "En attente"}</Col>
                    {order?.note && <Col span={12}><strong>Note:</strong> {order?.note}</Col>}
                </Row>
            </Card>

            <Divider>Confirmer les détails</Divider>

            <Row gutter={[16, 16]}>
                <Col xl={12} md={12} sm={24} xs={24}>
                    <SelectInput
                        name="confirmationStatus"
                        size="large"
                        placeholder="Statut de confirmation"
                        options={confirmationStatusOptions}
                        onChangeValue={handleConfirmationStatusChange}
                    />
                </Col>
                <Col xl={12} md={12} sm={24} xs={24}>
                    <TextInput
                        name="name"
                        placeholder="Nom du client"
                        size="large"
                    />
                </Col>
                <Col xl={12} md={12} sm={24} xs={24}>
                    <TextInput
                        name="phone"
                        placeholder="Téléphone"
                        size="large"
                    />
                </Col>
                <Col xl={12} md={12} sm={24} xs={24}>
                    <TextInput
                        name="price"
                        placeholder="Prix (DH)"
                        size="large"
                        type="number"
                        step="0.01"
                    />
                </Col>
                <Col xl={24} md={24} sm={24} xs={24}>
                    <TextInput
                        name="address"
                        placeholder="Adresse"
                        size="large"
                    />
                </Col>
                {selectedConfirmationStatus === 'confirmée' && <Col xl={12} md={12} sm={24} xs={24}>
                    <SelectInput
                        name="bmdCity"
                        size="large"
                        placeholder={order?.city || "Ville BMD"}
                        options={cities.map((city) => ({ value: city.id, label: city.name }))}
                    />
                </Col>}
                <Col span={24}>
                    <TextInput
                        name="note"
                        placeholder="Note"
                        size="large"
                    />
                </Col>
                {/* Multi-select products and units */}
                <Col xl={12} md={12} sm={24} xs={24}>
                    <div style={{ marginBottom: "8px", fontWeight: "500" }}>
                        Produits et unités:
                    </div>
                    <FieldArrayInput
                        name="orders"
                        fieldToAdd={{
                            product: undefined,
                            unit: null
                        }}
                        allowFirstInput={true}
                        renderInput={(index) => (
                            <Row gutter={[8, 8]}>
                                <Col xs={24} md={12} lg={12}>
                                    <SelectInput
                                        name={`orders[${index}].product`}
                                        size="large"
                                        placeholder={"Produit"}
                                        options={products}
                                    />
                                </Col>
                                <Col xs={24} md={12} lg={12}>
                                    <TextInput
                                        name={`orders[${index}].unit`}
                                        placeholder="Unité"
                                        size="large"
                                        type="number"
                                    />
                                </Col>
                            </Row>
                        )}
                    />
                </Col>

                {/* Show delivery fields only when confirmationStatus is "confirmée" */}
                {selectedConfirmationStatus === 'confirmée' && (
                    <>
                        <Col xl={12} md={12} sm={24} xs={24}>
                            <SelectInput
                                name="deliveryCompany"
                                size="large"
                                placeholder="Société de livraison"
                                options={deliveryCompanies}
                                onChangeValue={handleDeliveryCompanies}
                            />
                        </Col>
                        <Col xl={12} md={12} sm={24} xs={24}>
                            <SelectInput
                                name="deliveryCompanyKey"
                                size="large"
                                placeholder="Compte de livraison"
                                options={selectedDeliveryCompaniesAccounts}
                            />
                        </Col>
                    </>
                )}

                <Col xl={24} md={24} sm={24} xs={24}>
                    <Button
                        size='large'
                        type="primary"
                        htmlType="submit"
                        loading={loading}
                        style={{ width: "100%" }}
                    >
                        Confirmer la commande
                    </Button>
                </Col>
            </Row>
        </AddElementLayout>
    );
};

export default ConfirmOrderForm;
